import * as d3 from 'd3-selection'
import {scaleBand} from 'd3-scale'
import * as array from 'd3-array'

// TODO SCOTT TWEAKS
const labelBackgroundPadding = 5
const maxLabelBackgroundHeight = 25
const leftStagePadding = 10
const minFontSize = 7
const maxFontSize = 24

const midpoint = (p0, p1) => {
  return [
    (p0[0] + p1[0]) / 2,
    (p0[1] + p1[1]) / 2
  ]
}

const stage = (selection, config) => {
  selection.each(function (d) {
    let radius = d.labelContainerHeight / 2
      , group = d3.select(this)
                  .selectAll('g.stage-number')
                  .data(d => config.stage ? [d] : [])
      , enter = group.enter()
                     .append('g')
                     .attr('class', 'stage-number')

    group.exit().remove()

    enter.append('circle')
         .attr('r', radius)

    enter.append('text')

    group = group.merge(enter)

    group.selectAll('circle')
         .attr('r', radius)

    group.attr('transform', `translate(${radius}, ${radius})`)
         .select('text')
         .text(d => d.stage)
         .attr('font-size', radius * 1.5)
  })
}

const title = (selection, config) => {
  selection.each(function (d) {
    let text = d3.select(this)
                 .selectAll('g.text-group-title')
                 .data(d => d.title ? [d] : [])
                 .join('g')
                 .attr('class', 'text-group-title')
                 .attr('transform', d => `translate(${config.stage ? d.labelContainerHeight + leftStagePadding : leftStagePadding}, ${d.labelContainerHeight / 2})`)
                 .selectAll('text.title')
                  .data(d => [d])

    text.exit().remove()

    text.enter()
        .append('text')
        .attr('class', 'title')
        .merge(text)
        .style('font-size', d => d.fontSize)
        .attr('dy', d => {
          return d.hasValue ? '-.6em' : null
        })
        .text(d => d.title)
        .select(function (d) {
          let ellipsis = '...'
          // Crop it
          while (Math.floor(this.getComputedTextLength()) > d.maxWidth && this.textContent !== ellipsis) {
            d3.select(this).text(`${d3.select(this).text().slice(0, -4)}${ellipsis}`)
          }
        })
  })
}

const value = (selection, config) => {
  selection.each(function (d) {
    let text = d3.select(this)
                 .selectAll('g.text-group-value')
                 .data(d => config.value ? [d] : [])
                 .join('g')
                 .attr('class', 'text-group-value')
                 .attr('transform', d => `translate(${config.stage ? d.labelContainerHeight + leftStagePadding : leftStagePadding}, ${d.labelContainerHeight / 2})`)
                 .selectAll('text.value')
                 .data(d => [d])

    text.exit().remove()

    text.enter()
        .append('text')
        .attr('class', 'value')
        .merge(text)
        .style('font-size', d => d.fontSize)
        .attr('dy', d => {
          return d.hasTitle ? '.6em' : null
        })
        .text(d => d.formatter(d.raw))
  })
}

const background = (selection, config) => {
  selection.each(function (d) {
    // Add the background behind the labels
    let background = d3.select(this)
                       .selectAll('rect.label-background')
                       .data(d => [d])
                       .enter()
                       .insert('rect', ':first-child')
                       .attr('class', 'label-background')
                       .merge(d3.select(this).selectAll('rect.label-background'))
                       .style('display', 'none') // So we can calculate the actual width of labels. Will be toggled later

    let hasBackground = d.hasTitle || d.hasValue
      , backgroundRadius = (d.labelContainerHeight + labelBackgroundPadding * 2) * .5

    background.attr('width', function (d) {
                let width = this.parentNode.getBBox().width + labelBackgroundPadding * 2

                width += backgroundRadius * .5 // Since the rx/ry cuts the corner we need to pad the width
                this.parentNode.__width = width

                return width
              })
              .attr('height', d.labelContainerHeight + labelBackgroundPadding * 2)
              .attr('rx', backgroundRadius)
              .attr('ry', backgroundRadius)
              .attr('transform', `translate(${-labelBackgroundPadding}, ${-labelBackgroundPadding})`)
              .style('display', hasBackground ? '' : 'none')

  })
}

const getLabels = (selection, dimensions, stages, config, labelContainerHeight) => {
  let hasTitle = !!stages.find(stage => stage.title)
    , hasValue = !!stages.find(stage => config.value && stage.raw)
    , labelStub = selection.append('g')
                           .attr('class', 'stage-label')
                           .append('text')
                           .attr('class', 'title')

  stages.forEach(stage => {
    let innerHeight = labelContainerHeight
      , labels = [hasTitle && stage.title, hasValue && stage.title].filter(Boolean)
      , longestText = labels.map(String).reduce((a, b) => a.length > b.length ? a : b, '')
      , stageRadius = config.stage ? labelContainerHeight / 2 : 0
      , maxWidth = (dimensions.width / 2) - stageRadius * 2

    if (hasTitle && hasValue) {
      // Showing both title and value, so restrict the height
      innerHeight /= 2
    }

    labelStub.text(longestText)
             .attr('font-size', '1px') // So getComputedTextLength works

    // Determine the font size that fits in the ideal width
    let fontWidth = Math.min(maxWidth, maxWidth / labelStub.node().getComputedTextLength())
      , fontSize = Math.min(innerHeight, fontWidth) // Cap based on height

    stage.hasTitle = hasTitle
    stage.hasValue = hasValue
    stage.maxWidth = maxWidth
    stage.labelContainerHeight = labelContainerHeight
    stage.fontSize = fontSize
  })

  labelStub.remove()

  // What's the min font-size, so we can use it for ALL labels
  let fontSize = Math.min(maxFontSize, array.min(stages, stage => stage.fontSize))
  stages.forEach(stage => {
    stage.fontSize = Math.max(minFontSize, fontSize) // Make sure it's not too small
  })
}

export default (selection, config) => {
  let data = selection.datum()
    , stages = data._stages
    , labelScale = scaleBand().domain([0, stages.length]) // This scale is used to determine the height available for each label block
                              .range([0, data.dimensions.height])
                              .paddingOuter(.9) // Outer padding of the label box
                              .paddingInner(.45) // padding between each label box
    , labelContainerHeight = Math.min(labelScale.bandwidth(), maxLabelBackgroundHeight)
    , labels = getLabels(selection, data.dimensions, stages, config, labelContainerHeight)

  selection.each(function (d) {
    let container = d3.select(this)
                      .selectAll('g.stage-label')
                      .data(d => labelContainerHeight ? d._stages : [])

    container.exit().remove()

    container = container.enter()
                         .append('g')
                         .attr('class', 'stage-label')
                         .merge(container)
                         .attr('transform', d => {
                          return `translate(0, ${d.offset})`
                         })
                         .classed('is-hidden', false)

    // Add text labels
    container.call(stage, config)
             .call(title, config)
             .call(value, config)
             .call(background, config)

    // Position the container
    container.attr('transform', function (d) {
      // Grab the left path coords
      let coords = d.paths[0].coords
        , midX = midpoint(coords[1], coords[2]) // The x value is on the slanted line
        , midY = midpoint(coords[2], coords[0]) // The y value is between the top left point and the middle bottom point
        , width = this.__width
        , x = midX[0] - width / 2
        , y = midY[1] - labelContainerHeight / 2

      if ((x + width + labelBackgroundPadding * 2) > coords[0][0]) {
        x = coords[0][0] - width - labelBackgroundPadding
      }

      y = y + d.offset
      this._top = y
      return `translate(${Math.max(x, labelBackgroundPadding)}, ${y})`
    })

    // Check if any labels are overlapping
    container.sort((a, b) => a.raw - b.raw)
             .each(function (d) {
               // Find all the visible stage labels above this one
               let above = container.filter(function (stage) {
                   return stage.idx > d.idx && !d3.select(this).classed('is-hidden')
                 })
                 , top = this._top
                 , self = this

               // If any of the above labels have a bottom position lower than this top, hide this label
               above.each(function (d) {
                 let bottom = this.getBBox().height + this._top

                 if (bottom > top) {
                   d3.select(self).classed('is-hidden', true)
                 }
               })
             })


  })
}
